import React, { FC, useRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { gsap } from 'gsap/all'
import { singleAnnouncementAnimation } from 'src/components/sections/announcements/animations'

export interface ISingleAnnouncementData {
  status: string
  location: string
  position: string
  requirements: string
  mailSubject: string
}

interface ISingleAnnouncementProps extends ISingleAnnouncementData {
  action: string
  timeline: any
}

const SingleAnnouncement: FC<ISingleAnnouncementProps> = ({
  status,
  location,
  position,
  requirements,
  mailSubject,
  action,
  timeline,
}) => {
  const singleAnnouncementRef = useRef<HTMLDivElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
    if (cursorRef.current) {
      gsap.set(cursorRef.current, { xPercent: -50, yPercent: -50 })
    }
  }, [])

  singleAnnouncementAnimation(timeline, singleAnnouncementRef)

  const handleMouseMove = (e: React.MouseEvent) => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return

    if (cursorRef.current) {
      gsap.to(cursorRef.current, {
        x: e.clientX,
        y: e.clientY,
        duration: 0.15,
        ease: 'power2.out',
        overwrite: 'auto',
      })
    }
  }

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return

    if (cursorRef.current) {
      gsap.set(cursorRef.current, { x: e.clientX, y: e.clientY })
      gsap.to(cursorRef.current, {
        scale: 1,
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
        overwrite: 'auto',
      })
    }
  }

  const handleMouseLeave = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return

    if (cursorRef.current) {
      gsap.to(cursorRef.current, {
        scale: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
        overwrite: 'auto',
      })
    }
  }

  return (
    <div
      ref={singleAnnouncementRef}
      className="relative opacity-0 w-full md:aspect-video p-6 md:p-8 border-2 border-smallLightGray rounded-3xl flex flex-col justify-between hover:border-red transition-colors duration-300 group md:cursor-none overflow-hidden"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Custom Cursor Circle */}
      {isMounted && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={cursorRef}
            className="pointer-events-none fixed left-0 top-0 z-50 flex h-24 w-24 items-center justify-center rounded-full bg-red text-white opacity-0"
            style={{ transform: 'scale(0)' }}
          >
            <span className="font-palanquin text-lg font-bold uppercase tracking-widest">
              APPLY
            </span>
          </div>,
          document.body
        )
        : null}

      <div className="flex justify-between items-start">
        <span className="text-sm sm:text-base text-red font-palanquin font-bold tracking-widest uppercase">
          {status}
        </span>
        <span className="text-sm sm:text-lg text-mediumDarkGray font-semibold">
          {location}
        </span>
      </div>
      
      <div className="flex flex-col gap-6 md:gap-8 mt-auto">
        <div className="flex flex-col gap-4">
          <h3 className="font-palanquin text-2xl sm:text-4xl text-secondaryBlack font-bold leading-tight">
            {position}
          </h3>
          <p className="text-base sm:text-lg font-medium text-mediumDarkGray line-clamp-2">
            {requirements}
          </p>
        </div>
      </div>
      <a href={`mailto:studio@mamf.pl?subject=${mailSubject}`} className="absolute inset-0 z-20"></a>
    </div>
  )
}
export default SingleAnnouncement
